import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { profileAvatar } from "../component/ImageList";
import baseURL from "../component/baseURL";
import stringsoflanguages from "../component/stringsoflanguages";
import showNotification from "../component/Notification";
import { Toggle, GooeyFilter } from "../components/ui/liquid-toggle";

const TAB_MY_LISTINGS = "mylistings";
const TAB_MY_PORTFOLIO = "myportfolio";

export default function ConsumerAppSettings() {
    const navigate = useNavigate();
    const location = useLocation();

    const [userID, setUserID] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [activeTab, setActiveTab] = useState(TAB_MY_LISTINGS);
    const [myfeedActiveList, setMyfeedActiveList] = useState([]);
    const [multifeedList, setMultifeedList] = useState([]);
    const [myFeedToggle, setMyFeedToggle] = useState(false);
    const [multifeedToggle, setMultifeedToggle] = useState(false);
    /** From setting_agent_details root — Portfolio “Choose Agent” / “Consumer Chooses Agent” */
    const [portfolioChooseAgent, setPortfolioChooseAgent] = useState(false);
    const [portfolioConsumerChooses, setPortfolioConsumerChooses] = useState(false);
    const [openSettingsAgentId, setOpenSettingsAgentId] = useState("");
    const [fieldEditMode, setFieldEditMode] = useState({});
    const [fieldDraftByAgent, setFieldDraftByAgent] = useState({});
    const [managerDetails, setManagerDetails] = useState({});
    const [emailAddress, setEmailAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [mFullName, setMFullName] = useState("");
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    /** Staged base64 (data URL) for profile / company image before Save */
    const [profileImageBase64, setProfileImageBase64] = useState("");
    const [companyImageBase64, setCompanyImageBase64] = useState("");
    /** True while profile or company image upload API is in flight */
    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    /** Which image upload is running — for loader copy */
    const [imageUploadKind, setImageUploadKind] = useState(null);
    const gearMenuRef = useRef(null);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleMobileBack = useCallback(() => {
        const path = location.pathname.toLowerCase();
        if (path.includes("/premium-home")) {
            navigate("/premium-home");
        } else {
            navigate(-1);
        }
    }, [location.pathname, navigate]);

    const normalizeDisplayValue = useCallback((value) => {
        if (value == null) return "";
        const normalized = String(value).trim();
        if (!normalized) return "";
        const lowered = normalized.toLowerCase();
        if (lowered === "null" || lowered === "undefined") return "";
        return normalized;
    }, []);

    const parseToggleFlag = useCallback((value) => {
        if (value == null) return null;
        const normalized = String(value).trim().toLowerCase();
        if (normalized === "1" || normalized === "true") return true;
        if (normalized === "0" || normalized === "false") return false;
        return null;
    }, []);

    const resolveNonZeroId = useCallback(
        (...candidates) => {
            for (const c of candidates) {
                const v = normalizeDisplayValue(c);
                if (!v) continue;
                if (v === "0") continue;
                return v;
            }
            return "";
        },
        [normalizeDisplayValue]
    );

    const resolveImageUrl = useCallback(
        (val, subfolder) => {
            const s = normalizeDisplayValue(val);
            if (!s) return "";
            if (/^https?:\/\//i.test(s)) return s;
            if (/^data:/i.test(s)) return s;
            const folder = subfolder || "contactImage";
            return `https://www.yesmls.com/yes100/assets/upload/frontendImage/${folder}/${s}`;
        },
        [normalizeDisplayValue]
    );

    const sourceList = activeTab === TAB_MY_LISTINGS ? myfeedActiveList : multifeedList;

    const filteredConnections = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return sourceList;
        return sourceList.filter((item) => {
            const hay = [
                item?.fname,
                item?.lname,
                item?.fullname,
                item?.company_name,
                item?.city_name,
                item?.country_name,
                item?.email,
            ]
                .map((v) => normalizeDisplayValue(v).toLowerCase())
                .join(" ");
            return hay.includes(q);
        });
    }, [sourceList, searchText, normalizeDisplayValue]);

    const fetchDetailsAPI = useCallback(
        (userid) => {
            if (!userid) return Promise.resolve();
            const formdata = new FormData();
            formdata.append("user_id", userid);
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
            return fetch(baseURL.URL + "/portfolio/setting_agent_details", {
                method: "POST",
                body: formdata,
            })
                .then((response) => response.json())
                .then((responseJson) => {
                    const ok =
                        responseJson?.server?.result_code === 200 ||
                        responseJson?.server?.code === 200;
                    if (!ok) {
                        showNotification(
                            "Error",
                            responseJson?.server?.message || "Request failed",
                            "danger"
                        );
                        return;
                    }
                    const result = responseJson.response || {};
                    setMyfeedActiveList(
                        Array.isArray(result.myfeed_active) ? result.myfeed_active : []
                    );
                    setMultifeedList(Array.isArray(result.multifeed) ? result.multifeed : []);
                    setPortfolioChooseAgent(
                        Number(result.toggle_agent) === 1 ||
                        String(result.toggle_agent) === "true"
                    );
                    setPortfolioConsumerChooses(
                        Number(result.toggle_consumer) === 1 ||
                        String(result.toggle_consumer) === "true"
                    );
                })
                .catch((error) => {
                    console.error(error);
                    showNotification("Error", "Could not load settings.", "danger");
                });
        },
        []
    );

    const fetchSettingsAPI = useCallback(
        (userid) => {
            if (!userid) return Promise.resolve();
            const formdata = new FormData();
            formdata.append("user_id", userid);
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
            return fetch(baseURL.URL + "/portfolio/toggle_settings", {
                method: "POST",
                body: formdata,
            })
                .then((response) => response.json())
                .then((responseJson) => {
                    const ok =
                        responseJson?.server?.code === 200 ||
                        responseJson?.server?.result_code === 200;
                    if (!ok) {
                        return;
                    }
                    const result = responseJson.response;
                    if (result == null) return;
                    const nextMyFeed = parseToggleFlag(result.myfeed_active);
                    const nextMultiFeed = parseToggleFlag(result.multifeed);
                    const resolvedMyFeed = nextMyFeed ?? false;
                    const resolvedMultiFeed =
                        nextMultiFeed ?? (resolvedMyFeed ? false : true);

                    setMyFeedToggle(resolvedMyFeed);
                    setMultifeedToggle(resolvedMultiFeed);

                    if (resolvedMyFeed) {
                        setActiveTab(TAB_MY_LISTINGS);
                    } else if (resolvedMultiFeed) {
                        setActiveTab(TAB_MY_PORTFOLIO);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        [parseToggleFlag]
    );

    const fetchManager = useCallback((userid) => {
        if (!userid) return Promise.resolve();
        const formdata = new FormData();
        formdata.append("user_id", userid);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        return fetch(baseURL.URL + "/users/change_default", {
            method: "POST",
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const result = responseJson.response;
                console.log(result);
                if (responseJson.server.code === 200) {
                    setManagerDetails(result);
                    setEmailAddress(result.email);
                    setPhone(result.phone);
                    setMFullName(result.fullname);
                } else {
                    showNotification("Error", responseJson.server.message, "danger");
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        const userid = localStorage.getItem("user_id");
        setUserID(userid);
        if (!userid) {
            setLoading(false);
            return;
        }
        Promise.all([
            fetchDetailsAPI(userid),
            fetchSettingsAPI(userid),
            fetchManager(userid),
        ]).finally(() => {
            setLoading(false);
        });
    }, [fetchDetailsAPI, fetchSettingsAPI, fetchManager]);

    function changeSettingsAPI(myFeed, multiFeed) {
        if (!userID) return;
        const formdata = new FormData();
        formdata.append("user_id", userID);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        formdata.append("myfeed_active", myFeed);
        formdata.append("multifeed", multiFeed);
        fetch(baseURL.URL + "/portfolio/toggle_settings", {
            method: "POST",
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const ok =
                    responseJson?.server?.code === 200 ||
                    responseJson?.server?.result_code === 200;
                if (!ok) {
                    showNotification(
                        "Error",
                        responseJson?.server?.message || "Update failed",
                        "danger"
                    );
                    return;
                }
                setMyFeedToggle(myFeed);
                setMultifeedToggle(multiFeed);
                fetchSettingsAPI(userID);
                fetchDetailsAPI(userID);
            })
            .catch((error) => {
                console.error(error);
                showNotification("Error", "Could not update feed setting.", "danger");
            });
    }

    function updateManager(nextData = {}) {
        const nextFullName = normalizeDisplayValue(nextData.fullname) || mFullName;
        const nextPhone = normalizeDisplayValue(nextData.phone) || phone;
        const nextEmail = emailAddress;
        setMFullName(nextFullName);
        setPhone(nextPhone);
        setEmailAddress(nextEmail);

        let formdata = new FormData();
        formdata.append("user_id", userID)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("fullname", nextFullName)
        formdata.append("email", nextEmail)
        formdata.append("phone", nextPhone)
        fetch(baseURL.URL + '/users/change_default', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server.code === 200) {
                    showNotification("Success", responseJson.server.message || "Updated.", "success");
                }
                else {
                    showNotification("Error", responseJson.server.message, "danger");
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    function handleFieldSave(agent, key) {
        const uid = normalizeDisplayValue(agent?.user_id);
        if (key === "phone") {
            updateManager({ phone, fullname: mFullName });
        } else if (key === "leads") {
            updateManager({ fullname: mFullName, phone });
        }
        setFieldEditing(uid, key, false);
    }

    function handleImageUpload(fieldKey, file) {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            showNotification("Error", "Please choose an image file.", "danger");
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            if (typeof dataUrl !== "string") return;
            if (fieldKey === "picture") {
                setProfileImageBase64(dataUrl);
                setManagerDetails((prev) => ({
                    ...prev,
                    picture: dataUrl,
                    picture_thumb: dataUrl,
                }));
                return;
            }
            if (fieldKey === "logo") {
                setCompanyImageBase64(dataUrl);
                setManagerDetails((prev) => ({
                    ...prev,
                    company_image: dataUrl,
                    company_image_thumb: dataUrl,
                }));
            }
        };
        reader.onerror = () => {
            showNotification("Error", "Could not read the image file.", "danger");
        };
        reader.readAsDataURL(file);
    }

    function handleMainFeedSwitch(checked) {
        if (activeTab === TAB_MY_LISTINGS) {
            if (checked) changeSettingsAPI(true, false);
            else changeSettingsAPI(false, true);
        } else {
            if (checked) changeSettingsAPI(false, true);
            else changeSettingsAPI(true, false);
        }
    }

    function pushToggleAgentConsumer(chooseAgent, consumerChooses) {
        if (!userID) return;
        const formdata = new FormData();
        formdata.append("user_id", userID);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        formdata.append("toggle_agent", chooseAgent ? "true" : "false");
        formdata.append("toggle_consumer", consumerChooses ? "true" : "false");
        fetch(baseURL.URL + "/portfolio/toggle_agent_consumer", {
            method: "POST",
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const ok =
                    responseJson?.server?.code === 200 ||
                    responseJson?.server?.result_code === 200;
                if (!ok) {
                    showNotification(
                        "Error",
                        responseJson?.server?.message || "Update failed",
                        "danger"
                    );
                    return;
                }
                fetchDetailsAPI(userID);
            })
            .catch((error) => {
                console.error(error);
                showNotification("Error", "Could not update agent selection mode.", "danger");
            });
    }

    /** Portfolio: mutually exclusive modes when turning one ON; both can be OFF */
    function handlePortfolioChooseAgentSwitch(checked) {
        if (checked) pushToggleAgentConsumer(true, false);
        else pushToggleAgentConsumer(false, portfolioConsumerChooses);
    }

    function handlePortfolioConsumerChoosesSwitch(checked) {
        if (checked) pushToggleAgentConsumer(false, true);
        else pushToggleAgentConsumer(portfolioChooseAgent, false);
    }

    function chooseAgentDefault(subAgentID) {
        if (!userID) return;
        const formdata = new FormData();
        formdata.append("user_id", userID);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        formdata.append("sub_agent_id", subAgentID);
        fetch(baseURL.URL + "/portfolio/toggle_single_agent", {
            method: "POST",
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const ok =
                    responseJson?.server?.code === 200 ||
                    responseJson?.server?.result_code === 200;
                if (!ok) {
                    showNotification(
                        "Error",
                        responseJson?.server?.message || "Request failed",
                        "danger"
                    );
                    return;
                }
                fetchDetailsAPI(userID);
            })
            .catch((error) => {
                console.error(error);
                showNotification("Error", "Could not set default agent.", "danger");
            });
    }


    function updateProfileImage(imageBase64Param, onSuccess) {
        if (!userID) {
            showNotification("Error", "Please sign in again.", "danger");
            return;
        }
        const payload =
            typeof imageBase64Param === "string" && imageBase64Param
                ? imageBase64Param
                : profileImageBase64;
        if (!payload) {
            showNotification("Error", "Please select a profile image first.", "danger");
            return;
        }
        let formdata = new FormData();
        formdata.append("user_id", userID);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        formdata.append("profile_image", payload);
        setImageUploadKind("picture");
        setImageUploadLoading(true);
        fetch(baseURL.URL + "/users/change_default_profile_pic", {
            method: "POST",
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const ok =
                    responseJson?.server?.code === 200 ||
                    responseJson?.server?.result_code === 200;
                if (ok) {
                    setProfileImageBase64("");
                    fetchManager(userID);
                    showNotification(
                        "Success",
                        responseJson?.server?.message || "Profile image updated.",
                        "success"
                    );
                    if (typeof onSuccess === "function") onSuccess();
                } else {
                    showNotification(
                        "Error",
                        responseJson?.server?.message || "Update failed.",
                        "danger"
                    );
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification("Error", "Could not update profile image.", "danger");
            })
            .finally(() => {
                setImageUploadLoading(false);
                setImageUploadKind(null);
            });
    }

    function updateCompanyImage(imageBase64Param, onSuccess) {
        if (!userID) {
            showNotification("Error", "Please sign in again.", "danger");
            return;
        }
        const payload =
            typeof imageBase64Param === "string" && imageBase64Param
                ? imageBase64Param
                : companyImageBase64;
        if (!payload) {
            showNotification("Error", "Please select a company logo image first.", "danger");
            return;
        }
        let formdata = new FormData();
        formdata.append("user_id", userID);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        formdata.append("company_image", payload);
        setImageUploadKind("logo");
        setImageUploadLoading(true);
        fetch(baseURL.URL + "/users/change_default_company_pic", {
            method: "POST",
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const ok =
                    responseJson?.server?.code === 200 ||
                    responseJson?.server?.result_code === 200;
                if (ok) {
                    setCompanyImageBase64("");
                    fetchManager(userID);
                    showNotification(
                        "Success",
                        responseJson?.server?.message || "Company image updated.",
                        "success"
                    );
                    if (typeof onSuccess === "function") onSuccess();
                } else {
                    showNotification(
                        "Error",
                        responseJson?.server?.message || "Update failed.",
                        "danger"
                    );
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification("Error", "Could not update company image.", "danger");
            })
            .finally(() => {
                setImageUploadLoading(false);
                setImageUploadKind(null);
            });
    }

    /**
     * Portfolio tab per-agent toggle.
     * Backend endpoint is reused: when turning OFF, send `sub_agent_id = 0` to clear selection.
     */
    function setPortfolioAgentEnabled(subAgentID) {
        const sid = normalizeDisplayValue(subAgentID);
        if (!userID || !sid) return;
        chooseAgentDefault(sid);
    }

    function getSettingsDraft(agent) {
        const uid = normalizeDisplayValue(agent?.user_id);
        const fallback = {
            picture:
                resolveImageUrl(agent?.picture_thumb) ||
                resolveImageUrl(agent?.picture) ||
                profileAvatar,
            logo:
                resolveImageUrl(agent?.company_image_thumb, "companyImage") ||
                resolveImageUrl(agent?.company_image, "companyImage"),
            phone: normalizeDisplayValue(agent?.phone),
            email: normalizeDisplayValue(agent?.email),
            leads: normalizeDisplayValue(agent?.fullname) ||
                `${normalizeDisplayValue(agent?.fname)} ${normalizeDisplayValue(agent?.lname)}`.trim(),
        };
        return fieldDraftByAgent[uid] || fallback;
    }

    function handleSettingsToggle(agent) {
        const uid = normalizeDisplayValue(agent?.user_id);
        if (!uid) return;
        if (openSettingsAgentId === uid) {
            setOpenSettingsAgentId("");
            return;
        }
        setOpenSettingsAgentId(uid);
        setFieldDraftByAgent((prev) => {
            if (prev[uid]) return prev;
            return { ...prev, [uid]: getSettingsDraft(agent) };
        });
    }

    function setFieldDraftValue(agentId, key, value) {
        setFieldDraftByAgent((prev) => ({
            ...prev,
            [agentId]: {
                ...(prev[agentId] || {}),
                [key]: value,
            },
        }));
    }

    function setFieldEditing(agentId, key, editing) {
        setFieldEditMode((prev) => ({
            ...prev,
            [agentId]: {
                ...(prev[agentId] || {}),
                [key]: editing,
            },
        }));
    }

    const feedSwitchChecked =
        activeTab === TAB_MY_LISTINGS ? myFeedToggle : multifeedToggle;
    const openSettingsAgent = useMemo(
        () =>
            sourceList.find(
                (item) => normalizeDisplayValue(item?.user_id) === openSettingsAgentId
            ) || null,
        [sourceList, openSettingsAgentId, normalizeDisplayValue]
    );
    const modalSettingsUid = normalizeDisplayValue(openSettingsAgent?.user_id);
    const modalSettingsDraft = openSettingsAgent ? getSettingsDraft(openSettingsAgent) : {};
    const modalEditState = fieldEditMode[modalSettingsUid] || {};

    if (loading) {
        return (
            <div
                className={`content-right consumer-app-settings-page${isMobile ? " feed-recived-page-mobile" : ""} page-loader-wrapper`}
            >
                <div className="react-js-loader">
                    <div className="react-js-loader-spinner"></div>
                    <p className="react-js-loader-text">{stringsoflanguages.loading}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                className={`content-right consumer-app-settings-page${isMobile ? " feed-recived-page-mobile" : ""}`}
            >
                <GooeyFilter />
                {isMobile && (
                    <div className="mobile-page-header">
                        <button
                            type="button"
                            className="mobile-back-btn"
                            onClick={handleMobileBack}
                            aria-label="Back"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <h2 className="mobile-page-title">
                            {stringsoflanguages.consumerAppSettings}
                        </h2>
                    </div>
                )}
                <div className="cas-tabs" role="tablist" aria-label="Feed type">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === TAB_MY_LISTINGS}
                        className={`cas-tab ${activeTab === TAB_MY_LISTINGS ? "is-active" : ""}`}
                        onClick={() => setActiveTab(TAB_MY_LISTINGS)}
                    >
                        {stringsoflanguages.listings}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === TAB_MY_PORTFOLIO}
                        className={`cas-tab ${activeTab === TAB_MY_PORTFOLIO ? "is-active" : ""}`}
                        onClick={() => setActiveTab(TAB_MY_PORTFOLIO)}
                    >
                        {stringsoflanguages.portfolio}
                    </button>
                </div>

                <div className="toggle-row-container">
                    <div className="cas-toggle-row">
                        <span className="cas-toggle-label">
                            {activeTab === TAB_MY_LISTINGS
                                ? stringsoflanguages.myFeedActive
                                : stringsoflanguages.multiFeedActive}
                        </span>
                        <Toggle
                            checked={feedSwitchChecked}
                            onCheckedChange={handleMainFeedSwitch}
                            variant="default"
                        />
                    </div>

                    {activeTab === TAB_MY_PORTFOLIO ? (
                        <div className="cas-meta-card cas-portfolio-modes-card">
                            <div className="cas-meta-row">
                                <span className="cas-meta-label">{stringsoflanguages.chooseAgent}</span>
                                <Toggle
                                    checked={portfolioChooseAgent}
                                    onCheckedChange={handlePortfolioChooseAgentSwitch}
                                    variant="default"
                                />
                            </div>
                            {/* <div className="cas-meta-divider" /> */}
                            <div className="cas-meta-row">
                                <span className="cas-meta-label">{stringsoflanguages.consumerChooseAgent}</span>
                                <Toggle
                                    checked={portfolioConsumerChooses}
                                    onCheckedChange={handlePortfolioConsumerChoosesSwitch}
                                    variant="default"
                                />
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="search-filter-section cas-search-wrap">
                    <div className="search-bar">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            className="search-icon"
                            aria-hidden
                        >
                            <path
                                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M17.5 17.5L13.875 13.875"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <input
                            type="search"
                            placeholder="Search by Name"
                            className="search-input"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                </div>

                <span className="listing-count cas-count">
                    {stringsoflanguages.noofmembers}: {filteredConnections.length}
                </span>

                <div className={`cas-cards-stack ${activeTab === TAB_MY_PORTFOLIO ? "cas-cards-stack-portfolio" : "listings-card-stack"}`}>
                    {filteredConnections.map((agent) => {
                        const fname = normalizeDisplayValue(agent?.fname);
                        const lname = normalizeDisplayValue(agent?.lname);
                        const fullName = `${fname} ${lname}`.trim() || "N/A";
                        const city = normalizeDisplayValue(agent?.city_name);
                        const country = normalizeDisplayValue(agent?.country_name);
                        const locLine = [city, country].filter(Boolean).join(" ") || "—";
                        const profileSrc =
                            resolveImageUrl(agent?.picture_thumb) ||
                            resolveImageUrl(agent?.picture) ||
                            profileAvatar;
                        const companyRaw =
                            normalizeDisplayValue(agent?.company_image_thumb) ||
                            normalizeDisplayValue(agent?.company_image);
                        const companySrc = companyRaw
                            ? resolveImageUrl(companyRaw, "companyImage")
                            : "";
                        const isAdmin =
                            Number(agent?.is_admin) === 1 || String(agent?.is_admin) === "1";
                        const agentRowToggleOn =
                            Number(agent?.toggle_agent) === 1 || String(agent?.toggle_agent) === "1";
                        // API list items can be inconsistent and may contain placeholder `0` ids.
                        // For per-row portfolio toggle, always use the clicked row's real id (skip "0").
                        const uid = resolveNonZeroId(
                            agent?.user_id,
                            agent?.sub_agent_id,
                            agent?.agent_id,
                            agent?.consumer_id,
                            agent?.id
                        );
                        const canShowPortfolioToggle =
                            Number(agent?.subscription_type) === 1 ||
                            String(agent?.subscription_type) === "1";
                        const isPortfolioTab = activeTab === TAB_MY_PORTFOLIO;
                        const settingsOpen = !isPortfolioTab && openSettingsAgentId === uid;

                        return (
                            <div
                                className={`cas-agent-card${isPortfolioTab ? " cas-agent-card-portfolio" : " listings-card"}`}
                                key={uid || fullName}
                            >
                                <div className="cas-agent-top">
                                    <div className="cas-avatar">
                                        <img src={profileSrc} alt="" />
                                    </div>
                                    <div className="cas-agent-center">
                                        <div className="cas-agent-name">{fullName}</div>
                                        <div className="cas-agent-loc">{locLine}</div>

                                    </div>
                                    <div className="cas-company-thumb">
                                        {companySrc ? (
                                            <img src={companySrc} alt="" />
                                        ) : (
                                            <span className="cas-company-fallback">Co.</span>
                                        )}
                                    </div>
                                </div>
                                {isPortfolioTab ? (
                                    <div className="cas-portfolio-card-footer">
                                        {isAdmin ? (
                                            <div className={`cas-admin-pill${isPortfolioTab ? " is-badge" : ""}`}>
                                                Admin
                                            </div>
                                        ) : null}
                                        {canShowPortfolioToggle ? (
                                            <Toggle
                                                checked={agentRowToggleOn}
                                                onCheckedChange={() =>
                                                    setPortfolioAgentEnabled(agent?.user_id)
                                                }
                                                aria-label={`Select agent ${fullName}`}
                                                disabled={!uid}
                                                variant="default"
                                            />
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="cas-card-actions">
                                        <button
                                            type="button"
                                            className={`cas-btn cas-btn-default${agentRowToggleOn ? " is-selected" : ""}`}
                                            onClick={() => chooseAgentDefault(uid)}
                                            disabled={!uid}
                                        >
                                            Default
                                        </button>
                                        <button
                                            type="button"
                                            className="cas-btn cas-btn-settings"
                                            onClick={() => handleSettingsToggle(agent)}
                                            disabled={!uid}
                                        >
                                            {settingsOpen ? "Close" : "Settings"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!isMobile && (
                    <div className="btn-section btn-section-gear" ref={gearMenuRef}>
                        <button
                            type="button"
                            className={`btn-primary gear-btn ${gearSpinning ? "gear-spin" : ""}`}
                            onClick={() => {
                                setGearMenuOpen((prev) => !prev);
                                setGearSpinning(true);
                                setTimeout(() => setGearSpinning(false), 500);
                            }}
                            aria-label="Options"
                            aria-expanded={gearMenuOpen}
                        >
                            <i className="fa-solid fa-gear" />
                        </button>
                        {gearMenuOpen && (
                            <ul className="gear-dropdown" aria-label="Options list">
                                <li>
                                    <button
                                        type="button"
                                        className="gear-dropdown-item"
                                        onClick={() => navigate("/listing-layout-consumer")}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="22"
                                            height="22"
                                            viewBox="0 0 22 22"
                                            fill="none"
                                        >
                                            <path
                                                d="M19.7083 9.99183V3.7585C19.7083 2.3835 19.1217 1.8335 17.6642 1.8335H13.9608C12.5033 1.8335 11.9167 2.3835 11.9167 3.7585V9.99183C11.9167 11.3668 12.5033 11.9168 13.9608 11.9168H17.6642C19.1217 11.9168 19.7083 11.3668 19.7083 9.99183Z"
                                                fill="black"
                                            />
                                            <path
                                                d="M10.0833 12.0085V18.2418C10.0833 19.6168 9.49666 20.1668 8.03916 20.1668H4.33582C2.87832 20.1668 2.29166 19.6168 2.29166 18.2418V12.0085C2.29166 10.6335 2.87832 10.0835 4.33582 10.0835H8.03916C9.49666 10.0835 10.0833 10.6335 10.0833 12.0085Z"
                                                fill="black"
                                            />
                                            <path
                                                d="M19.7083 18.2417V15.675C19.7083 14.3 19.1217 13.75 17.6642 13.75H13.9608C12.5033 13.75 11.9167 14.3 11.9167 15.675V18.2417C11.9167 19.6167 12.5033 20.1667 13.9608 20.1667H17.6642C19.1217 20.1667 19.7083 19.6167 19.7083 18.2417Z"
                                                fill="black"
                                            />
                                            <path
                                                d="M10.0833 6.32516V3.7585C10.0833 2.3835 9.49666 1.8335 8.03916 1.8335H4.33582C2.87832 1.8335 2.29166 2.3835 2.29166 3.7585V6.32516C2.29166 7.70016 2.87832 8.25016 4.33582 8.25016H8.03916C9.49666 8.25016 10.0833 7.70016 10.0833 6.32516Z"
                                                fill="black"
                                            />
                                        </svg>
                                        <span>Listing Layout</span>
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                )}

                {isMobile && (
                    <nav
                        className="mobile-bottom-nav liquidGlass-wrapper dock feed-recived-mobile-bottom-nav"
                        aria-label="Consumer app settings actions"
                    >
                        <div className="liquidGlass-effect" aria-hidden="true" />
                        <div className="liquidGlass-shine" aria-hidden="true" />
                        <div className="mobile-nav-item" aria-label="Listing Layout" onClick={() => navigate("/listing-layout-consumer")}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 22 22"
                                fill="none"
                            >
                                <path
                                    d="M19.7083 9.99183V3.7585C19.7083 2.3835 19.1217 1.8335 17.6642 1.8335H13.9608C12.5033 1.8335 11.9167 2.3835 11.9167 3.7585V9.99183C11.9167 11.3668 12.5033 11.9168 13.9608 11.9168H17.6642C19.1217 11.9168 19.7083 11.3668 19.7083 9.99183Z"
                                    fill="black"
                                />
                                <path
                                    d="M10.0833 12.0085V18.2418C10.0833 19.6168 9.49666 20.1668 8.03916 20.1668H4.33582C2.87832 20.1668 2.29166 19.6168 2.29166 18.2418V12.0085C2.29166 10.6335 2.87832 10.0835 4.33582 10.0835H8.03916C9.49666 10.0835 10.0833 10.6335 10.0833 12.0085Z"
                                    fill="black"
                                />
                                <path
                                    d="M19.7083 18.2417V15.675C19.7083 14.3 19.1217 13.75 17.6642 13.75H13.9608C12.5033 13.75 11.9167 14.3 11.9167 15.675V18.2417C11.9167 19.6167 12.5033 20.1667 13.9608 20.1667H17.6642C19.1217 20.1667 19.7083 19.6167 19.7083 18.2417Z"
                                    fill="black"
                                />
                                <path
                                    d="M10.0833 6.32516V3.7585C10.0833 2.3835 9.49666 1.8335 8.03916 1.8335H4.33582C2.87832 1.8335 2.29166 2.3835 2.29166 3.7585V6.32516C2.29166 7.70016 2.87832 8.25016 4.33582 8.25016H8.03916C9.49666 8.25016 10.0833 7.70016 10.0833 6.32516Z"
                                    fill="black"
                                />
                            </svg>
                        </div>
                    </nav>
                )}

                {openSettingsAgent ? (
                    <div
                        className="cas-settings-modal-overlay"
                        onClick={() => {
                            if (imageUploadLoading) return;
                            setOpenSettingsAgentId("");
                        }}
                    >
                        <div
                            className="cas-settings-modal-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {imageUploadLoading ? (
                                <div
                                    className="cas-settings-modal-upload-loader"
                                    role="status"
                                    aria-live="polite"
                                    aria-busy="true"
                                >
                                    <div className="loading-spinner" aria-hidden="true" />
                                    <span className="cas-settings-modal-upload-loader-text">
                                        {imageUploadKind === "logo"
                                            ? "Uploading company logo…"
                                            : "Uploading profile picture…"}
                                    </span>
                                </div>
                            ) : null}
                            <div className="cas-settings-modal-header">
                                <h3>{stringsoflanguages.agentSettings}</h3>
                                <button
                                    type="button"
                                    className="cas-settings-modal-close"
                                    disabled={imageUploadLoading}
                                    onClick={() => {
                                        if (imageUploadLoading) return;
                                        setOpenSettingsAgentId("");
                                    }}
                                    aria-label="Close settings"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="cas-settings-panel">
                                <div className="cas-settings-grid-two">
                                    <div className="cas-setting-box">
                                        <div className="cas-setting-header">
                                            <span className="cas-setting-title">{stringsoflanguages.pic}</span>
                                            <button
                                                type="button"
                                                className={`cas-edit-icon-btn${modalEditState.picture ? " is-save" : ""}`}
                                                disabled={imageUploadLoading}
                                                onClick={() => {
                                                    if (imageUploadLoading) return;
                                                    if (modalEditState.picture) {
                                                        if (profileImageBase64) {
                                                            updateProfileImage(profileImageBase64, () =>
                                                                setFieldEditing(modalSettingsUid, "picture", false)
                                                            );
                                                        } else {
                                                            setFieldEditing(modalSettingsUid, "picture", false);
                                                        }
                                                        return;
                                                    }
                                                    setProfileImageBase64("");
                                                    setFieldEditing(modalSettingsUid, "picture", true);
                                                }}
                                            >
                                                {modalEditState.picture ? (
                                                    <i className="fa-regular fa-floppy-disk" />
                                                ) : (
                                                    <i className="fa-regular fa-pen-to-square" />
                                                )}
                                            </button>
                                        </div>
                                        <div className="cas-setting-body cas-setting-image">
                                            <img
                                                src={
                                                    resolveImageUrl(managerDetails?.picture) ||
                                                    modalSettingsDraft.picture ||
                                                    profileAvatar
                                                }
                                                alt=""
                                            />
                                        </div>
                                        {modalEditState.picture ? (
                                            <input
                                                className="cas-setting-input"
                                                type="file"
                                                accept="image/*"
                                                disabled={imageUploadLoading}
                                                onChange={(e) =>
                                                    handleImageUpload("picture", e.target.files?.[0])
                                                }
                                            />
                                        ) : null}
                                    </div>
                                    <div className="cas-setting-box">
                                        <div className="cas-setting-header">
                                            <span className="cas-setting-title">{stringsoflanguages.logo}</span>
                                            <button
                                                type="button"
                                                className={`cas-edit-icon-btn${modalEditState.logo ? " is-save" : ""}`}
                                                disabled={imageUploadLoading}
                                                onClick={() => {
                                                    if (imageUploadLoading) return;
                                                    if (modalEditState.logo) {
                                                        if (companyImageBase64) {
                                                            updateCompanyImage(companyImageBase64, () =>
                                                                setFieldEditing(modalSettingsUid, "logo", false)
                                                            );
                                                        } else {
                                                            setFieldEditing(modalSettingsUid, "logo", false);
                                                        }
                                                        return;
                                                    }
                                                    setCompanyImageBase64("");
                                                    setFieldEditing(modalSettingsUid, "logo", true);
                                                }}
                                            >
                                                {modalEditState.logo ? (
                                                    <i className="fa-regular fa-floppy-disk" />
                                                ) : (
                                                    <i className="fa-regular fa-pen-to-square" />
                                                )}
                                            </button>
                                        </div>
                                        <div className="cas-setting-body cas-setting-image">
                                            <img
                                                src={
                                                    resolveImageUrl(managerDetails?.company_image, "companyImage") ||
                                                    modalSettingsDraft.logo ||
                                                    profileAvatar
                                                }
                                                alt=""
                                            />
                                        </div>
                                        {modalEditState.logo ? (
                                            <input
                                                className="cas-setting-input"
                                                type="file"
                                                accept="image/*"
                                                disabled={imageUploadLoading}
                                                onChange={(e) =>
                                                    handleImageUpload("logo", e.target.files?.[0])
                                                }
                                            />
                                        ) : null}
                                    </div>
                                </div>

                                {[
                                    [stringsoflanguages.phone, "phone"],
                                    [stringsoflanguages.email, "email"],
                                    [stringsoflanguages.leads, "leads"],
                                ].map(([label, key]) => (
                                    <div className="cas-setting-box cas-setting-row" key={key}>
                                        <div className="cas-setting-header">
                                            <span className="cas-setting-title">{label}</span>
                                            <button
                                                type="button"
                                                className={`cas-edit-icon-btn${modalEditState[key] ? " is-save" : ""}`}
                                                disabled={key === "email"}
                                                onClick={() => {
                                                    if (key === "email") return;
                                                    if (modalEditState[key]) {
                                                        handleFieldSave(openSettingsAgent, key);
                                                        return;
                                                    }
                                                    setFieldEditing(modalSettingsUid, key, true);
                                                }}
                                            >
                                                {modalEditState[key] ? (
                                                    <i className="fa-regular fa-floppy-disk" />
                                                ) : (
                                                    <i className="fa-regular fa-pen-to-square" />
                                                )}
                                            </button>
                                        </div>
                                        {modalEditState[key] ? (
                                            <input
                                                className="cas-setting-input"
                                                type="text"
                                                value={
                                                    key === "phone"
                                                        ? phone
                                                        : key === "email"
                                                            ? emailAddress
                                                            : mFullName
                                                }
                                                disabled={key === "email"}
                                                onChange={(e) => {
                                                    if (key === "phone") {
                                                        setPhone(e.target.value);
                                                        return;
                                                    }
                                                    if (key === "leads") {
                                                        setMFullName(e.target.value);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="cas-setting-value">
                                                {(key === "phone"
                                                    ? normalizeDisplayValue(phone) || normalizeDisplayValue(managerDetails?.phone)
                                                    : key === "email"
                                                        ? normalizeDisplayValue(emailAddress) || normalizeDisplayValue(managerDetails?.email)
                                                        : normalizeDisplayValue(mFullName) || normalizeDisplayValue(managerDetails?.fullname)) || "—"}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null}

                {filteredConnections.length === 0 ? (
                    <p className="cas-empty">{stringsoflanguages.noAgentsInThisFeed}</p>
                ) : null}
            </div>
            <svg style={{ display: 'none' }} aria-hidden="true">
                <filter id="glass-distortion" x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                    <feTurbulence type="fractalNoise" baseFrequency="0.01 0.01" numOctaves="1" seed="5" result="turbulence" />
                    <feComponentTransfer in="turbulence" result="mapped">
                        <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                        <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
                        <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
                    </feComponentTransfer>
                    <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                    <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                        <fePointLight x="-200" y="-200" z="300" />
                    </feSpecularLighting>
                    <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                    <feDisplacementMap in="SourceGraphic" in2="softMap" scale="150" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </svg>
        </>
    );
}
